import React, { Component, KeyboardEvent, useCallback, useMemo, useState } from "react";
import { BaseLoginCard, LoginOptions } from "../Login/BaseLoginScreen";
import Strings, { LVStrings } from "@my-dld/assets/strings";
import { navigate } from "@my-dld/utils/StoreUtils";
import { PickerItem } from "@my-dld/components/Picker";
import { getLocalValue, LocaleValue } from "@eres/core/dist/LocaleValue";
import TextInputField from "@my-dld/components/Form/TextInputField";
import { ErrorContainer } from "@eres/forms";
import NumberConverter from "@my-dld/components/Form/Converters/NumberConverter";
import { I8N, StringUtil } from "@eres/core";
import { AppConstants } from "../../configApp";
import { DNRDModel, RegistrationApi } from "@10x/tabu-mobile-api";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { convertError } from "@my-dld/utils/error-utils";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import VerifyPopup from "../Register/VerifyPopup";
import { useRegContext } from "./ctx";
import { checkExceedLimit, DNRDModelX } from "./utils";

interface RegType extends PickerItem {
	hint?: LocaleValue;
	docType: number;
	maxLength?: number;
	minLength?: number;
	test?: RegExp;
	errorMsg?: LocaleValue;
}

const RegTracks: RegType[] = [
	{
		id: "eid",
		value: LVStrings.emirates_id,
		hint: { en: "784-xxxx-xxxxxxx-x" },
		docType: 1,
		maxLength: 15,
		minLength: 15,
		test: /^784/,
		errorMsg: LVStrings.please_enter_valid_eid,
	},
	{
		id: "uid",
		value: LVStrings.unified_number,
		hint: { en: "12345" },
		docType: 2,
		maxLength: 25,
		minLength: 4,
		errorMsg: LVStrings.please_enter_valid_unified_number,
	},
	{
		id: "visa",
		value: LVStrings.residence_visa_number,
		hint: { en: "12345" },
		docType: 3,
		maxLength: 25,
		minLength: 4,
		errorMsg: LVStrings.please_enter_valid_residence_visa_number,
	},
];

interface FormState {
	id?: string;
	errorContainer: ErrorContainer;
	savedIds: Record<string, string>;
}

interface FormProps {
	id: string;
	regType: RegType;
	onNext: ( id: string ) => void;
}

class Form extends Component<FormProps, FormState> {
	constructor( props: FormProps ) {
		super( props );
		this.state = {
			errorContainer: {},
			savedIds: {},
			id: ""
		};
	}

	onNext = () => {
		let { onNext, regType } = this.props;
		const errorContainer: ErrorContainer = {};
		let isValid = true;
		const val = this.state.id;
		// isValid = isValid && checkNonEmpty( this.state, "id", errorContainer );
		if ( StringUtil.isEmptyStringOrNull( val ) ) {
			isValid = false;
			errorContainer[ "id" ] = regType.errorMsg ? getLocalValue( regType.errorMsg ) : Strings.invalid;
		}
		if ( val ) {
			const errMsg = regType.errorMsg ? getLocalValue( regType.errorMsg ) : Strings.invalid;
			if ( regType.minLength != null ) {
				if ( val?.length < regType.minLength ) {
					errorContainer[ "id" ] = errMsg;
					isValid = false;
				}
			}
			if ( isValid && regType.maxLength != null ) {
				if ( val.length > regType.maxLength ) {
					errorContainer[ "id" ] = errMsg;
					isValid = false;
				}
			}
			if ( isValid && regType.test != null ) {
				if ( !regType.test.test( val ) ) {
					errorContainer[ "id" ] = errMsg;
					isValid = false;
				}
			}
		}
		this.setState( { errorContainer: errorContainer } );
		if ( isValid ) {
			onNext( this.state.id! );
		}
	};
	onKeyUp = ( event: KeyboardEvent<any> ) => {
		if ( event.charCode === 13 ) {
			event.preventDefault();
			this.onNext();
		}
	};

	componentDidUpdate( prevProps: Readonly<FormProps>, prevState: Readonly<FormState>, snapshot?: any ) {
		if ( prevProps.id !== this.props.id ) {
			const newId = this.props.id;
			const oldIdT = prevProps.id;
			this.setState( s => {
				const oldId = s.savedIds[ newId ] ?? "";
				return {
					errorContainer: {},
					id: oldId,
					savedIds: {
						...s.savedIds,
						[ oldIdT ]: s.id,
					},
				} as FormState;
			} );
		}
	}

	render() {
		let { id, regType } = this.props;

		const channelName = getLocalValue( regType?.value );
		const channelHint = getLocalValue( regType?.hint ) ?? "";
		return <div className="auth-container-form" id={ id }>
			<TextInputField
				containerClassName={ "form-group" }
				inputClassName={ "form-control" }
				placeHolder={ channelHint }
				id={ "id" }
				holder={ this }
				error={ this.state.errorContainer?.id }
				label={ channelName }
				maxLength={ regType?.maxLength }
				inputProps={ { onKeyPress: this.onKeyUp } }
				converter={ NumberConverter }
				required={ true }
			>
			</TextInputField>
			<div className="form-group">
				<button
					className="btn btn_style1 btn_1 btn-block fs-5" id="btnNext"
					onClick={ this.onNext }
				>
					{ Strings.next } </button>
			</div>
		</div>;
	}
}


async function checkForDuplicateEid( profile: DNRDModel ) {
	const eid = profile.EmiratesId!;
	try {
		const canContinue = await RegistrationApi.checkEmiratesIdExist( eid );
		if ( canContinue ) return profile;
		else throw "duplicate_record";
	} catch ( e ) {
		trackEventWithData( "Register", "DuplicateRegistration", { id: eid } );
		throw e;
	}
}

async function checkExpiredId( profile: DNRDModel ) {
	const eidExp = profile.EIDExpiryDate!;
	if ( eidExp ) {
		const exp = eidExp.asDate();
		if ( !checkExceedLimit( exp ) ) {
			throw "eid_expired_date";
		}
		return profile;
	}
	throw "eid_expired_date";
}

async function validateApi( profile: DNRDModel ) {
	try {
		return checkForDuplicateEid( profile ).then( checkExpiredId );
	} catch ( e ) {
		throw e;
	}
}

export function Search() {
	const [ searchType, setSearchType ] = useState<RegType | undefined>( () => {
		return RegTracks.find( x => x.id === "eid" );
	} );
	const [ maskedProfile, setMaskedProfile ] = useState<DNRDModel | null>( null );
	const [ showOTP, setShowOTP ] = useState( false );
	const regCtx = useRegContext();
	const channelId = searchType?.id;

	const onTypeChange = useCallback( ( id: string ) => {
		setSearchType( RegTracks.find( x => x.id === id ) );
	}, [] );
	const onLogin = useCallback( () => {
		navigate( "/login/owner" );
	}, [] );
	const onNext = useCallback( ( id: string ) => {
		if ( searchType ) {
			showLoading();
			return RegistrationApi.getProfileFromEIDA( searchType.docType, id, true )
			.then( r => {
				hideLoading();
				setMaskedProfile( r.Response );
				setShowOTP( true );
			} ).catch( reason => {
				hideLoading();
				if ( reason && reason.info && reason.info.Code === "11" ) {
					setMaskedProfile( reason.info.Response );
					setShowOTP( true );
				} else {
					showErrorMessage( convertError( reason ) );
				}
			} );


		}
	}, [ searchType ] );
	const UAEPassIcon = useMemo( () => `Button_UAE_Pass_${ I8N.locale() }_register`, [] );
	const onOTPDismiss = useCallback( () => {
		setShowOTP( false );
	}, [] );
	const onProfileVerified = useCallback( ( profile: DNRDModelX ) => {
		// profile.EmiratesId = "784198793024093";
		showLoading();
		validateApi( profile ).then( p => {
			hideLoading();
			regCtx.setProfile( profile );
			navigate( "/register/forms" );
		} ).catch( e => {
			hideLoading();
			showErrorMessage( convertError( e ) );
		} );
	}, [ regCtx ] );
    return <>{/*<BaseLoginCard id={ "individualUser" }>
		<div className="auth-title">
			<span>{ Strings.register_individual_via }</span>
			<button className="btn-link btn-switch-user" onClick={ onLogin }>{ Strings.login_as_individual } </button>
		</div>
		<div className="msg-owner-register">
			<span>{ Strings.register_popup_info_1 }</span>
			<span>{ " " }</span>
			<span>{ Strings.register_popup_info_2 }</span>
		</div>
		<LoginOptions
			items={ RegTracks } checkedId={ channelId ?? "" }
			onChange={ onTypeChange }/>
		{ searchType != null && <Form id={ channelId! } regType={ searchType } onNext={ onNext }/> }

		<div className="uae-pass-wrapper">
			<div className="sso-or-div"><span>{ Strings.or_caps }</span></div>
			<button className="btn">
				<img
					src={ `${ AppConstants.rootUrl }/assets/img/${ UAEPassIcon }.png` } className="img-fluid"
					alt="UAE Pass"/>
			</button>
		</div>
		{ showOTP && maskedProfile && <VerifyPopup
			mobile={ maskedProfile.Mobile! }
			profileHash={ maskedProfile.ResponseHash! }
			onDismiss={ onOTPDismiss }
            onVerified={onProfileVerified} />}

        <div className="row">
            
        </div>
    </BaseLoginCard>;*/}
    <div className="container mw-1100 ind-register">
        <div className="card col-uaepass">
            <div className="auth-title">
                <span>{Strings.register_individual_via}</span>
            </div>
            <div className="uaepass-infodiv">
                <img src={`${AppConstants.rootUrl}/assets/img/uae-pass.svg`} alt="UAE Pass Logo" />
            </div>
            <div className="text-center">
                UAE PASS is a single trusted<br />
                                    national digital identity for all citizens, <br />
                                    residents and visitors.
                                </div>
                <button className="btn btn-signup-uaepass" >
                    <span className="sr-only">UAE Pass Registration</span>
            </button>
            <div className="uaepass-registration">
                <span>
                    How to Register for UAE PASS? <a href="https://www.youtube.com/watch?v=M62taRz58f0" rel="noopener noreferrer" target="_blank">Watch Video</a>
                </span>
            </div>
        </div>
    </div></>
}
